/**
 * Layout-validator för AI-genererade workflows. Kör mekaniska geometri-
 * regler från `documents/v1/SCHEMA_LAYOUT_RULES.md` och returnerar
 * konkreta brott som kan skickas tillbaka till AI:n som retry-feedback.
 *
 * Regler (sammanfattning — full spec i docs):
 *   - Block-koordinater (x, y) måste vara multiples of 20 (grid-snap).
 *   - Block-rektanglar (200×100, center-origin) får inte överlappa.
 *   - Block bör vara minst 280 px isär horisontellt eller 140 px vertikalt.
 *   - Grupp-bounds måste innesluta alla member-block med ≥ 40 px padding.
 *   - Grupp-bounds får inte överlappa varandra (≥ 40 px luft).
 *   - Connector-portar bör matcha geometrin (höger-höger inom rad,
 *     uppifrån-ner mellan rader).
 *   - Max 5 block per rad, max 8 block per grupp.
 *
 * `severity = 'error'` blockerar accept; `'warning'` rapporteras men
 * fail:ar inte validering. `ok = true` när inga errors finns.
 */
import type {
  WorkflowState,
  MultiSheetWorkflowState,
  WorkflowBlock,
  BlockGroup,
  Connector,
} from '@/types/workflow';
import { isMultiSheetState } from '@/types/workflow';

export interface LayoutViolation {
  /** Maskinläsbar regel-identifier — t.ex. "block-overlap". */
  rule: string;
  severity: 'error' | 'warning';
  /** Naturligt språk-beskrivning för feedback till AI:n. */
  message: string;
  blockIds?: string[];
  groupIds?: string[];
  connectorIds?: string[];
  /** Sheet-index om multi-sheet (saknas för single-sheet). */
  sheetIndex?: number;
}

export interface LayoutValidationResult {
  ok: boolean;
  errorCount: number;
  warningCount: number;
  violations: LayoutViolation[];
}

// ── Konstanter (matchar SCHEMA_LAYOUT_RULES.md) ─────────────────────
export const GRID_SIZE = 20;
export const BLOCK_WIDTH = 200;
export const BLOCK_HEIGHT = 100;
export const MIN_HORIZONTAL_SPACING = 280;
export const MIN_VERTICAL_SPACING = 140;
export const MAX_HORIZONTAL_SPACING = 400;
export const MAX_VERTICAL_SPACING = 240;
export const GROUP_PADDING = 40;
export const MIN_GROUP_GAP = 40;
export const MAX_BLOCKS_PER_ROW = 5;
export const MAX_BLOCKS_PER_GROUP = 8;

interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

function blockRect(b: WorkflowBlock): Rect {
  return {
    left: b.x - BLOCK_WIDTH / 2,
    top: b.y - BLOCK_HEIGHT / 2,
    right: b.x + BLOCK_WIDTH / 2,
    bottom: b.y + BLOCK_HEIGHT / 2,
  };
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return !(a.right <= b.left || b.right <= a.left || a.bottom <= b.top || b.bottom <= a.top);
}

function groupRect(g: BlockGroup): Rect | null {
  if (
    g.boundsLeft === undefined ||
    g.boundsTop === undefined ||
    g.boundsRight === undefined ||
    g.boundsBottom === undefined
  ) return null;
  return {
    left: g.boundsLeft,
    top: g.boundsTop,
    right: g.boundsRight,
    bottom: g.boundsBottom,
  };
}

function validateSheet(
  blocks: WorkflowBlock[],
  connectors: Connector[],
  groups: BlockGroup[],
  sheetIndex: number | undefined,
): LayoutViolation[] {
  const out: LayoutViolation[] = [];
  const blockMap = new Map(blocks.map((b) => [b.id, b]));

  // 1. Grid-snap
  for (const b of blocks) {
    if (b.x % GRID_SIZE !== 0 || b.y % GRID_SIZE !== 0) {
      out.push({
        rule: 'grid-snap',
        severity: 'error',
        message: `Block "${b.config?.name ?? b.id}" har position (${b.x}, ${b.y}) som inte snap:ar till grid (${GRID_SIZE} px). Avrunda till närmaste multiple of ${GRID_SIZE}.`,
        blockIds: [b.id],
        sheetIndex,
      });
    }
  }

  // 2. Block-överlapp (hard error)
  for (let i = 0; i < blocks.length; i++) {
    for (let j = i + 1; j < blocks.length; j++) {
      if (rectsOverlap(blockRect(blocks[i]), blockRect(blocks[j]))) {
        out.push({
          rule: 'block-overlap',
          severity: 'error',
          message: `Block "${blocks[i].config?.name ?? blocks[i].id}" och "${blocks[j].config?.name ?? blocks[j].id}" överlappar varandra fysiskt. Förskjut minst ett av dem med ≥ ${MIN_HORIZONTAL_SPACING} px horisontellt eller ≥ ${MIN_VERTICAL_SPACING} px vertikalt.`,
          blockIds: [blocks[i].id, blocks[j].id],
          sheetIndex,
        });
      }
    }
  }

  // 3. Block-spacing (warning — för nära men ej överlapp)
  for (let i = 0; i < blocks.length; i++) {
    for (let j = i + 1; j < blocks.length; j++) {
      const a = blocks[i];
      const b = blocks[j];
      const dx = Math.abs(a.x - b.x);
      const dy = Math.abs(a.y - b.y);
      if (rectsOverlap(blockRect(a), blockRect(b))) continue; // redan rapporterat som error
      // Båda axlar för nära → spacing-warning
      if (dx < MIN_HORIZONTAL_SPACING && dy < MIN_VERTICAL_SPACING) {
        out.push({
          rule: 'block-too-close',
          severity: 'warning',
          message: `Block "${a.config?.name ?? a.id}" och "${b.config?.name ?? b.id}" ligger ${dx} px / ${dy} px isär — för tätt. Mål: ≥ ${MIN_HORIZONTAL_SPACING} px horisontellt eller ≥ ${MIN_VERTICAL_SPACING} px vertikalt.`,
          blockIds: [a.id, b.id],
          sheetIndex,
        });
      }
    }
  }

  // 4. Max blocks per row
  const blocksPerY = new Map<number, WorkflowBlock[]>();
  for (const b of blocks) {
    const arr = blocksPerY.get(b.y) ?? [];
    arr.push(b);
    blocksPerY.set(b.y, arr);
  }
  for (const [y, list] of blocksPerY) {
    if (list.length > MAX_BLOCKS_PER_ROW) {
      out.push({
        rule: 'too-many-blocks-per-row',
        severity: 'warning',
        message: `${list.length} block ligger på samma rad (y=${y}). Max ${MAX_BLOCKS_PER_ROW} per rad — splitta i flera rader eller flytta till en ny sheet.`,
        blockIds: list.map((b) => b.id),
        sheetIndex,
      });
    }
  }

  // 5. Group bounds-omslutning av member-blocks
  for (const g of groups) {
    if (g.collapsed) continue; // collapsed layers renderas som single card, inte frame
    const gRect = groupRect(g);
    if (!gRect) continue; // bounds inte satta — auto-beräknas vid render
    if (g.blockIds.length > MAX_BLOCKS_PER_GROUP) {
      out.push({
        rule: 'too-many-blocks-per-group',
        severity: 'warning',
        message: `Grupp "${g.name ?? g.id}" har ${g.blockIds.length} block (max ${MAX_BLOCKS_PER_GROUP}). Splitta i flera grupper eller flytta till en ny sheet.`,
        groupIds: [g.id],
        sheetIndex,
      });
    }
    for (const bid of g.blockIds) {
      const b = blockMap.get(bid);
      if (!b) continue;
      const br = blockRect(b);
      if (
        br.left < gRect.left ||
        br.top < gRect.top ||
        br.right > gRect.right ||
        br.bottom > gRect.bottom
      ) {
        out.push({
          rule: 'block-outside-group-bounds',
          severity: 'error',
          message: `Block "${b.config?.name ?? b.id}" sticker ut utanför sin grupp "${g.name ?? g.id}". Utöka grupp-bounds (boundsLeft/Top/Right/Bottom) så det innesluter alla member-block med ${GROUP_PADDING} px padding.`,
          blockIds: [b.id],
          groupIds: [g.id],
          sheetIndex,
        });
      }
    }
  }

  // 6. Group-group-överlapp
  const visibleGroups = groups.filter((g) => !g.collapsed && groupRect(g));
  for (let i = 0; i < visibleGroups.length; i++) {
    for (let j = i + 1; j < visibleGroups.length; j++) {
      const ga = visibleGroups[i];
      const gb = visibleGroups[j];
      const ra = groupRect(ga)!;
      const rb = groupRect(gb)!;
      // Tillåt nesting: om en grupps parentGroupId pekar på den andra → skip
      if (ga.parentGroupId === gb.id || gb.parentGroupId === ga.id) continue;
      if (rectsOverlap(ra, rb)) {
        out.push({
          rule: 'group-overlap',
          severity: 'error',
          message: `Grupp "${ga.name ?? ga.id}" och "${gb.name ?? gb.id}" överlappar varandra. Flytta en av dem så grupp-bounds har ≥ ${MIN_GROUP_GAP} px luft mellan sig.`,
          groupIds: [ga.id, gb.id],
          sheetIndex,
        });
      }
    }
  }

  // 7. Connector port-konsistens
  for (const c of connectors) {
    const a = blockMap.get(c.fromId);
    const b = blockMap.get(c.toId);
    if (!a || !b) continue; // dangling connector — annan validering hanterar det
    const sameRow = a.y === b.y;
    const sameCol = a.x === b.x;
    const fp = c.fromPort;
    const tp = c.toPort;
    // Om connectorn saknar portar är det ok — auto-routing tar över
    if (!fp || !tp) continue;

    // Same-row → bör vara r→l (eller l→r om bakåt)
    if (sameRow && a.x !== b.x) {
      const goingRight = b.x > a.x;
      const expectedFp = goingRight ? 'right' : 'left';
      const expectedTp = goingRight ? 'left' : 'right';
      if (fp !== expectedFp || tp !== expectedTp) {
        out.push({
          rule: 'connector-port-mismatch',
          severity: 'warning',
          message: `Connector ${c.id} (${a.config?.name ?? a.id} → ${b.config?.name ?? b.id}) ligger på samma rad men använder portar ${fp}→${tp}. Rekommenderat: ${expectedFp}→${expectedTp} för raka horisontella pilar.`,
          connectorIds: [c.id],
          blockIds: [a.id, b.id],
          sheetIndex,
        });
      }
    }
    // Same-column → bör vara b→t (eller t→b)
    if (sameCol && a.y !== b.y) {
      const goingDown = b.y > a.y;
      const expectedFp = goingDown ? 'bottom' : 'top';
      const expectedTp = goingDown ? 'top' : 'bottom';
      if (fp !== expectedFp || tp !== expectedTp) {
        out.push({
          rule: 'connector-port-mismatch',
          severity: 'warning',
          message: `Connector ${c.id} (${a.config?.name ?? a.id} → ${b.config?.name ?? b.id}) ligger i samma kolumn men använder portar ${fp}→${tp}. Rekommenderat: ${expectedFp}→${expectedTp} för raka vertikala pilar.`,
          connectorIds: [c.id],
          blockIds: [a.id, b.id],
          sheetIndex,
        });
      }
    }
    // Diagonal-portar bör inte överskrida 1 rad/kolumn
    const diagonalPorts = new Set(['top-right', 'top-left', 'bottom-right', 'bottom-left']);
    if (diagonalPorts.has(fp) || diagonalPorts.has(tp)) {
      const dx = Math.abs(a.x - b.x);
      const dy = Math.abs(a.y - b.y);
      if (dx > MAX_HORIZONTAL_SPACING || dy > MAX_VERTICAL_SPACING) {
        out.push({
          rule: 'connector-long-diagonal',
          severity: 'warning',
          message: `Connector ${c.id} (${a.config?.name ?? a.id} → ${b.config?.name ?? b.id}) använder diagonalportar (${fp}→${tp}) över ${dx} px × ${dy} px. Diagonaler ska vara max 1 rad + 1 kolumn isär — annars omplacera blocken eller välj rakta portar.`,
          connectorIds: [c.id],
          blockIds: [a.id, b.id],
          sheetIndex,
        });
      }
    }
  }

  return out;
}

export function validateLayout(
  state: WorkflowState | MultiSheetWorkflowState,
): LayoutValidationResult {
  const all: LayoutViolation[] = [];

  if (isMultiSheetState(state)) {
    state.sheets.forEach((sheet, idx) => {
      all.push(...validateSheet(sheet.blocks, sheet.connectors, sheet.groups, idx));
    });
  } else {
    all.push(...validateSheet(state.blocks, state.connectors, state.groups, undefined));
  }

  const errorCount = all.filter((v) => v.severity === 'error').length;
  const warningCount = all.filter((v) => v.severity === 'warning').length;

  return {
    ok: errorCount === 0,
    errorCount,
    warningCount,
    violations: all,
  };
}

/**
 * Formatera violations som en kompakt user-message-text att skicka tillbaka
 * till AI:n. Grupperar per regel så LLM:en inte får en flat lista på 50 rader.
 */
export function formatViolationsForAi(result: LayoutValidationResult): string {
  if (result.ok && result.warningCount === 0) {
    return 'Layout-validering: inga brott.';
  }
  const byRule = new Map<string, LayoutViolation[]>();
  for (const v of result.violations) {
    const key = `${v.severity}:${v.rule}`;
    const arr = byRule.get(key) ?? [];
    arr.push(v);
    byRule.set(key, arr);
  }

  const lines: string[] = [];
  lines.push(
    `Layout-validering hittade ${result.errorCount} error(s) och ${result.warningCount} warning(s). ` +
    `Generera om workflow-JSON:en så att alla errors åtgärdas. Behåll samma block-namn och flöde — ändra ENDAST positioner, grupp-bounds och connector-portar.`,
  );
  lines.push('');
  for (const [key, list] of byRule) {
    const [severity, rule] = key.split(':');
    lines.push(`### ${severity.toUpperCase()} — ${rule} (${list.length})`);
    // Visa max 5 instanser per regel; resten räknas
    const sample = list.slice(0, 5);
    for (const v of sample) {
      const sheet = v.sheetIndex !== undefined ? `[sheet ${v.sheetIndex}] ` : '';
      lines.push(`- ${sheet}${v.message}`);
    }
    if (list.length > sample.length) {
      lines.push(`- (+${list.length - sample.length} fler instanser av samma regel)`);
    }
    lines.push('');
  }
  lines.push(
    'Påminnelse om reglerna: blocken är 200×100 px (center-origin); minimum 280 px horisontellt eller 140 px vertikalt mellan center-punkter; alla x/y % 20 = 0; grupp-bounds måste innesluta alla member-block med 40 px padding och får inte överlappa andra grupp-bounds; använd r→l-portar inom samma rad och b→t mellan rader.',
  );
  return lines.join('\n');
}
